import useSWR from 'swr';
import { storeService } from 'services';
import { GeoPoint, KycStatus } from 'types';
import { Loading } from 'components';
import { RegulatoryInformation } from './components';
import { ENV, localEnv } from 'commons';

const KILOMETER = 1000;
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_SECRET;

interface Props {
  geoPoint: GeoPoint;
  isStoreShippingAble: boolean;
  isPickUpAble: boolean;
  kycStatus: KycStatus;
}

export function StoreMapAndRegulatoryInformation({
  geoPoint,
  isStoreShippingAble,
  isPickUpAble,
  kycStatus,
}: Props) {
  const { latitude, longitude } = geoPoint;
  const isGeoLocationAvailable = Boolean(latitude) && Boolean(longitude);

  const { data, isValidating } = useSWR(
    'base-store-data',
    () => storeService.getBaseStore(),
    { revalidateOnFocus: false }
  );

  if (!data || isValidating) {
    return (
      <div className="flex flex-1 min-w-80">
        <Loading />
      </div>
    );
  }

  if (!isGeoLocationAvailable) {
    return <span>Não foi possível exibir o mapa.</span>;
  }

  const { pickUpConfiguration, storeShippingConfiguration } = data;

  const radius = (() => {
    const defaultRadius = 0;

    if (isStoreShippingAble) return pickUpConfiguration.radius;

    return isPickUpAble ? storeShippingConfiguration.radius : defaultRadius;
  })();

  const radiusDistance = radius * KILOMETER;
  const size = '740x280';

  function buildGoogleStaticMapUrl() {
    const uri = 'https://maps.googleapis.com/maps/api/staticmap?';
    let staticMapSrc = `center=${latitude},${longitude}`;
    staticMapSrc += `&markers=color:red|${latitude},${longitude}`;
    staticMapSrc += `&size=${size}`;
    staticMapSrc += '&scale=2';
    staticMapSrc += `&key=${GOOGLE_MAPS_KEY}`;
    staticMapSrc += '&path=fillcolor:0x246EC333|color:0xff0000ff:weight:1';
    staticMapSrc += drawCircleRadius();

    return uri + encodeURI(staticMapSrc);
  }

  function drawCircleRadius() {
    const earthMeanRadius = 6371 * KILOMETER;
    const pi = Math.PI;
    const lat = (latitude * pi) / 180;
    const lng = (longitude * pi) / 180;
    const d = radiusDistance / earthMeanRadius;
    const coords = [];
    const circleDetail = 8;

    for (let i = 0; i <= 360; i += circleDetail) {
      let brng = (i * pi) / 180;

      let polylineLat = Math.asin(
        Math.sin(lat) * Math.cos(d) +
          Math.cos(lat) * Math.sin(d) * Math.cos(brng)
      );
      let polylineLng =
        ((lng +
          Math.atan2(
            Math.sin(brng) * Math.sin(d) * Math.cos(lat),
            Math.cos(d) - Math.sin(lat) * Math.sin(polylineLat)
          )) *
          180) /
        pi;
      polylineLat = (polylineLat * 180) / pi;

      coords.push([polylineLat, polylineLng]);
    }

    return '|enc:' + createEncodings(coords);
  }

  function createEncodings(coords: number[][]) {
    var plat = 0;
    var plng = 0;

    var encoded_points = '';

    for (let i = 0; i < coords.length; ++i) {
      var lat = coords[i][0];
      var lng = coords[i][1];

      encoded_points += encodePoint(plat, plng, lat, lng);

      plat = lat;
      plng = lng;
    }

    return encoded_points;
  }

  function encodePoint(plat: number, plng: number, lat: number, lng: number) {
    var dlng = 0;
    var dlat = 0;

    var late5 = Math.round(lat * 1e5);
    var plate5 = Math.round(plat * 1e5);

    var lnge5 = Math.round(lng * 1e5);
    var plnge5 = Math.round(plng * 1e5);

    dlng = lnge5 - plnge5;
    dlat = late5 - plate5;

    return encodeSignedNumber(dlat) + encodeSignedNumber(dlng);
  }

  function encodeSignedNumber(num: number) {
    var sgn_num = num << 1;

    if (num < 0) {
      sgn_num = ~sgn_num;
    }

    return encodeNumber(sgn_num);
  }

  function encodeNumber(num: number) {
    var encodeString = '';

    while (num >= 0x20) {
      encodeString += String.fromCharCode((0x20 | (num & 0x1f)) + 63);
      num >>= 5;
    }

    encodeString += String.fromCharCode(num + 63);
    return encodeString;
  }

  const urlMap =
    ENV === localEnv ? '/staticmap.png' : buildGoogleStaticMapUrl();

  return (
    <div className="flex flex-col flex-1 gap-6 min-w-72">
      <a
        href={`https://www.google.com.br/maps?q=${latitude},${longitude}`}
        target="_blank"
        rel="noreferrer"
      >
        <img
          className="rounded-md w-full object-cover"
          src={urlMap}
          alt="Mapa com raio de alcance da loja"
        />
      </a>

      <RegulatoryInformation kycStatus={kycStatus} />
    </div>
  );
}
